import React from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap } from '@atlaskit/css';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';
import { UI_EVENT_TYPE } from '@atlassian/analytics-bridge/constants';

import { useIsTranslationSupportEnabled } from '../../../../controllers/use-is-translation-support-enabled/use-is-translation-support-enabled';
import { useHubTranslationsContext } from '../hub-translations-context';
import { useManageTranslationsOpen } from '../manage-translations-open-context';
import { messages } from './messages';

export type ManageTranslationsButtonProps = {
	/** The ADF extensionType of the block (e.g. `"com.atlassian.confluence.carousel"`). */
	extensionType: string;
	/**
	 * When provided, opens the view scoped to this item (e.g. a carousel card) so only
	 * that item's fields are shown. Omit to show the whole extension's fields.
	 */
	itemId?: string;
};

const styles = cssMap({
	button: {
		alignItems: 'center',
		borderRadius: token('radius.small'),
		display: 'flex',
		height: '32px',
		justifyContent: 'center',
		'&:hover': {
			backgroundColor: token('color.background.accent.gray.subtlest.hovered'),
		},
		'&:focus': {
			backgroundColor: token('color.background.accent.gray.subtlest.hovered'),
		},
		transition: token('motion.button.hovered'),
	},
});

export const ManageTranslationsButton = ({
	extensionType,
	itemId,
}: ManageTranslationsButtonProps): React.JSX.Element | null => {
	const hubTranslationsCtx = useHubTranslationsContext();
	const supportedTranslationLanguages = hubTranslationsCtx?.supportedLanguages ?? {};
	const { open } = useManageTranslationsOpen();
	const isTranslationEnabled = useIsTranslationSupportEnabled();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (!isTranslationEnabled) {
		return null;
	}

	if (Object.keys(supportedTranslationLanguages).length === 0) {
		return null;
	}

	const handleClick = () => {
		open(itemId);
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'manageTranslations',
			analyticsType: UI_EVENT_TYPE,
			attributes: { extensionType },
		}).fire();
	};

	return (
		<ErrorBoundary fallback={null}>
			<Pressable onClick={handleClick} xcss={styles.button}>
				<Flex columnGap="space.050" alignItems="center">
					<FormattedMessage {...messages.buttonLabel} />
					<ChevronRightIcon color={token('color.icon.subtle')} label="" />
				</Flex>
			</Pressable>
		</ErrorBoundary>
	);
};
